/**
 * Shared types for the Requests IDE: the attorney's workspace for a client's
 * outgoing intakes (the questionnaires the firm emails the client), mounted in
 * the client profile's Requests tab in the Case IDE's grammar: an explorer on
 * the left, artifact tabs in the center, the Client Manager on the right.
 *
 * An "artifact" is anything that opens as a center tab: an intake (its
 * questions, or the read-only view of a sent one), the client-facing preview
 * of an intake, a submission under review, or a template from the catalog.
 */

import type { ClientInvalidateTarget } from "@/features/caseIde/types";

export type RequestArtifactKind =
  | "intake"
  | "preview"
  | "submission"
  | "template";

export const REQUEST_ARTIFACT_KINDS: readonly RequestArtifactKind[] = [
  "intake",
  "preview",
  "submission",
  "template"
];

export type RequestArtifact = {
  kind: RequestArtifactKind;
  /**
   * `intake` / `preview`: the intake id. `submission`: `<intakeId>/<submissionId>`.
   * `template`: a catalog `template_id` or `saved:<template_id>`.
   */
  id: string;
  title: string;
};

export const artifactKey = (a: Pick<RequestArtifact, "kind" | "id">): string =>
  `${a.kind}:${a.id}`;

export const parseArtifactKey = (
  key: string
): Pick<RequestArtifact, "kind" | "id"> | null => {
  const idx = key.indexOf(":");
  if (idx <= 0) return null;
  const kind = key.slice(0, idx) as RequestArtifactKind;
  if (!REQUEST_ARTIFACT_KINDS.includes(kind)) return null;
  const id = key.slice(idx + 1);
  return id ? { kind, id } : null;
};

/** `intakeId/submissionId` from a submission artifact id. */
export const parseSubmissionId = (
  id: string
): { intakeId: string; submissionId: string } | null => {
  const idx = id.indexOf("/");
  if (idx <= 0 || idx === id.length - 1) return null;
  return { intakeId: id.slice(0, idx), submissionId: id.slice(idx + 1) };
};

export const submissionArtifactId = (
  intakeId: string,
  submissionId: string
): string => `${intakeId}/${submissionId}`;

/** What the chat learns about the workspace with every turn. */
export type RequestsSelection = {
  artifact: RequestArtifact | null;
  /** Question node ids selected in the open intake's step list. */
  selectedNodeIds: string[];
  /** The answer key the attorney is looking at in the open submission. */
  selectedAnswerKey: string | null;
};

/** Caches the Requests IDE holds; a superset of the client-scoped ones. */
export type RequestsInvalidateTarget =
  | ClientInvalidateTarget
  | "templates"
  | "submissions";

export type RequestsInvalidateListener = (
  targets: RequestsInvalidateTarget[]
) => void;

/**
 * What a deep link or a menu asks the workspace to do, beyond opening a page:
 * start a new request a certain way. `saved` is one of the firm's templates,
 * created through the template send path so its defaults come along.
 */
export type NewRequestAction =
  | { kind: "blank" }
  | { kind: "templates"; templateIds: string[] }
  | { kind: "saved"; templateId: string }
  | { kind: "ai"; prompt?: string }
  | { kind: "docs"; visa: string };
